import { ApiClientProvider } from '@portals/sip-client-data/src/general/ApiClient';
import { EstateContactMessageProps, EstateDetail } from '@portals/sip-client-data/src/general/ApiClientTypes';
import { getFixedT } from 'i18next';
import { useSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';

import { Logger, trackBrokerContactSent } from '../../../../../utils';
import { EstateLegalModal, LegalDialogTypes } from '../../EstateLegalModal/EstateLegalModal';
import { EstateContactForm } from './EstateContactForm/EstateContactForm';
import { EstateContactFormSuccess } from './EstateContactFormSuccess/EstateContactFormSuccess';

export interface Props {
  estate: EstateDetail;
  showHeadline: boolean;
  onClose: () => void;
}

const t = getFixedT(null, 'core-immobilien');

export const EstateContact = ({ estate, showHeadline, onClose }: Props): React.ReactElement => {
  const { id: estateId, broker, contact } = estate;

  const { firstName, lastName } = broker;

  const privacyPolicy = contact?.privacyPolicy ?? '';
  const consumerInformation = contact?.consumerInformation;

  const [legalDialogProps, setLegalDialogProps] = useState({
    open: false,
    dialogType: null as LegalDialogTypes,
    content: null as string,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const closeLegalDialog = useCallback(() => {
    setLegalDialogProps((prevState) => {
      return { ...prevState, open: false };
    });
  }, []);

  const showPrivacyPolicy = useCallback(
    (event) => {
      event.preventDefault();
      setLegalDialogProps({
        open: true,
        dialogType: 'privacyPolicy',
        content: privacyPolicy,
      });
    },
    [privacyPolicy]
  );

  const showConsumerInformation = useCallback(
    (event) => {
      event.preventDefault();
      setLegalDialogProps({
        open: true,
        dialogType: 'consumerInformation',
        content: consumerInformation,
      });
    },
    [consumerInformation]
  );

  const sendContactMessage = useCallback(
    async (contactMessage: EstateContactMessageProps) => {
      const isSendSuccessful = await ApiClientProvider.getApiClient().clientSendEstateContactMessage(contactMessage);

      if (isSendSuccessful) {
        setIsSubmitted(true);
      } else {
        enqueueSnackbar(t('contactForm.generalError'), { variant: 'error' });
        Logger.error({ Message: '[IMMOBILIEN] unable to send estate contact message' });
      }
    },
    [enqueueSnackbar]
  );

  const onSubmit = useCallback(
    async (values) => {
      await sendContactMessage({
        estateId: estateId,
        instituteCode: '',
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        subject: values.subject,
        message: values.message,
        street: values.street,
        streetNumber: values.streetNumber,
        zip: values.zip,
        city: values.city,
      });

      setSubmittedEmail(values.email);
      trackBrokerContactSent(estate);
    },
    [estate, sendContactMessage]
  );

  if (isSubmitted && submittedEmail) {
    return (
      <EstateContactFormSuccess firstName={firstName} lastName={lastName} email={submittedEmail} onClose={onClose} />
    );
  }

  return (
    <>
      <EstateContactForm
        estate={estate}
        showHeadline={showHeadline}
        showPrivacyPolicy={showPrivacyPolicy}
        showConsumerInformation={showConsumerInformation}
        onSubmit={onSubmit}
      />

      {legalDialogProps.dialogType && (
        <EstateLegalModal
          open={legalDialogProps.open}
          onClose={closeLegalDialog}
          dialogType={legalDialogProps.dialogType}
          content={legalDialogProps.content}
        />
      )}
    </>
  );
};

EstateContact.displayName = 'EstateContact';
